import './style/mcpServer.scss';

import React from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import Image from '../misc/image';
import {BluemateButton} from '../controls';
import McpExampleChat from './exampleChat/mcpExampleChat';

interface Props {
    serverUrl: string;
}

interface Feature {
    id: string;
    icon: string;
    title: string;
    text: string;
}

export default function McpServer(props: Props) {
    const {serverUrl} = props;
    const {t} = useTranslation();

    const features: Feature[] = [
        {
            id: 'ask',
            icon: 'mdi:chat-question-outline',
            title: t('mcpServer.featureAskTitle', 'Vraag het gewoon'),
            text: t(
                'mcpServer.featureAskText',
                'Stel je vraag in gewone taal. Je AI assistent gebruikt de tools in de MCP om het antwoord te vinden.'
            )
        },
        {
            id: 'safe',
            icon: 'mdi:lock-check-outline',
            title: t('mcpServer.featureSafeTitle', 'Toegang & Veilig'),
            text: t(
                'mcpServer.featureSafeText',
                'Je kiest zelf welke rechten de agent heeft. Bijvoorbeeld alleen-lezen, zodat de agent geen wijzigingen kan maken.'
            )
        },
        {
            id: 'clients',
            icon: 'mdi:robot-outline',
            title: t('mcpServer.featureClientsTitle', 'Werkt met elke AI'),
            text: t(
                'mcpServer.featureClientsText',
                'Claude, ChatGPT, Gemini, Cursor en elke andere AI-assistent die MCP ondersteunt.'
            )
        }
    ];

    function renderOpenButton(label: string) {
        return (
            <BluemateButton
                className='open-button'
                href={serverUrl}
                label={label}
                ornaments={{element: <Icon icon='mdi:open-in-new' className='open-button-icon' />}}
            />
        );
    }

    function renderHero() {
        return (
            <header className='mcp-hero'>
                <div className='hero-text'>
                    <span className='hero-eyebrow'>{t('mcpServer.eyebrow', 'MCP Server')}</span>
                    <h1 className='hero-title'>
                        {t('mcpServer.title', 'Verbind jouw eigen AI met bol en Bluemate data')}
                    </h1>
                    <p className='hero-intro'>
                        {t(
                            'mcpServer.intro',
                            'Geef je AI-assistent, zoals ChatGPT of Claude, toegang tot Bluemate en bol.'
                        )}
                        <br />
                        {t('mcpServer.introDiscover', 'Stel vragen en krijg antwoorden via deze MCP verbinding.')}
                    </p>
                    <div className='server-cta'>
                        {renderOpenButton(t('mcpServer.installButton', 'Lees hoe te installeren'))}
                    </div>
                </div>
                <div className='hero-visual'>
                    <Image name='logo-no-text-big' alt='' className='hero-logo' />
                </div>
            </header>
        );
    }

    function renderFeature(feature: Feature) {
        return (
            <div key={feature.id} className='feature-card'>
                <Icon icon={feature.icon} className='feature-icon' />
                <h3 className='feature-title'>{feature.title}</h3>
                <p className='feature-text'>{feature.text}</p>
            </div>
        );
    }

    function renderFeatures() {
        return (
            <section className='features-section'>
                <h2 className='section-heading'>{t('mcpServer.featuresTitle', 'Wat kan je ermee?')}</h2>
                <div className='features-grid'>{features.map(renderFeature)}</div>
            </section>
        );
    }

    function renderExamples() {
        return (
            <section className='examples-section'>
                <h2 className='section-heading'>{t('mcpServer.examplesTitle', 'Voorbeeld van vragen')}</h2>
                <McpExampleChat />
                <span className='chat-disclaimer'>
                    {t('mcpServer.chatDisclaimer', 'Voorbeeldgesprekken met geanonimiseerde demo-data.')}
                </span>
            </section>
        );
    }

    function renderBottomCta() {
        return (
            <section className='bottom-cta'>
                <h2 className='section-heading'>
                    {t('mcpServer.bottomTitle', 'Verbind je AI-assistent met Bluemate')}
                </h2>
                <p className='bottom-cta-text'>
                    {t(
                        'mcpServer.bottomText',
                        'Open de MCP-pagina voor installatie-instructies per assistent en een overzicht van alle beschikbare tools.'
                    )}
                </p>
                {renderOpenButton(t('mcpServer.bottomButton', 'Open de MCP-pagina'))}
            </section>
        );
    }

    return (
        <div className='mcp-server-page'>
            {renderHero()}
            {renderFeatures()}
            {renderExamples()}
            {renderBottomCta()}
        </div>
    );
}
